import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, ExternalLink, X, Loader2, FolderOpen } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';

// Normalize a task's output field(s) into a single array so legacy
// single-link tasks keep working alongside the modern multi-link shape.
export const getOutputLinks = (task) => {
  const arr = Array.isArray(task?.output_links) ? task.output_links.filter(Boolean) : [];
  if (arr.length) return arr;
  if (task?.output_link) return [task.output_link];
  return [];
};

const normalizeUrl = (s) => {
  const v = (s || '').trim();
  if (!v) return '';
  return v.startsWith('http://') || v.startsWith('https://') ? v : `https://${v}`;
};

// Reusable output-links cell used by both Task Log and Brand Workspace.
// Supports multi-link add/remove with an inline popover and a shortcut
// to the Asset Library filtered on the task id.
export const OutputLinksCell = ({ task, onSave }) => {
  const links = getOutputLinks(task);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState([]);
  const [newUrl, setNewUrl] = useState('');
  const [busy, setBusy] = useState(false);

  const startEdit = () => {
    setDraft(links.length ? [...links] : []);
    setNewUrl('');
    setOpen(true);
  };

  const addCurrent = () => {
    const v = (newUrl || '').trim();
    if (!v) return;
    if (draft.includes(v)) {
      setNewUrl('');
      return;
    }
    setDraft([...draft, v]);
    setNewUrl('');
  };

  const removeAt = (idx) => setDraft(draft.filter((_, i) => i !== idx));

  const save = async () => {
    setBusy(true);
    // Include current input if user typed but didn't press +
    let final = [...draft];
    const v = (newUrl || '').trim();
    if (v && !final.includes(v)) final.push(v);
    await onSave(task.id, final);
    setBusy(false);
    setOpen(false);
  };

  return (
    <div className="inline-flex items-center gap-1">
      <Popover open={open} onOpenChange={(o) => (o ? startEdit() : setOpen(false))}>
        <PopoverTrigger asChild>
          {links.length === 0 ? (
            <button
              type="button"
              className="inline-flex items-center gap-1 px-2 h-7 rounded-md border border-dashed border-slate-300 text-[11px] text-slate-400 hover:text-[#2EB4AC] hover:border-[#2EB4AC] transition-colors"
              data-testid={`output-add-${task.id}`}
            >
              <Plus className="h-3 w-3" />
              Add link
            </button>
          ) : (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-2 h-7 rounded-md bg-[#2EB4AC]/8 hover:bg-[#2EB4AC]/15 border border-[#2EB4AC]/20 text-[11px] font-medium text-[#2EB4AC] transition-colors"
              title={links.join('\n')}
              data-testid={`output-view-${task.id}`}
            >
              <ExternalLink className="h-3 w-3" />
              <span>View</span>
              {links.length > 1 && (
                <span className="ml-0.5 px-1.5 rounded-full bg-[#2EB4AC] text-white text-[9px] font-bold leading-tight">
                  {links.length}
                </span>
              )}
            </button>
          )}
        </PopoverTrigger>
        <PopoverContent className="w-80 p-3" align="start">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-slate-700">Output links</p>
              {links.length > 0 && (
                <span className="text-[10px] text-slate-400">{draft.length} {draft.length === 1 ? 'link' : 'links'}</span>
              )}
            </div>

            {/* Existing links */}
            {draft.length > 0 && (
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {draft.map((url, idx) => (
                  <div key={`${url}-${idx}`} className="flex items-center gap-1.5 group" data-testid={`output-row-${task.id}-${idx}`}>
                    <a
                      href={normalizeUrl(url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 truncate text-[11px] text-[#2EB4AC] hover:underline"
                      title={url}
                    >
                      {url}
                    </a>
                    <button
                      type="button"
                      onClick={() => removeAt(idx)}
                      className="w-5 h-5 rounded-md text-slate-300 hover:bg-red-50 hover:text-red-500 flex items-center justify-center transition-colors"
                      title="Remove"
                      data-testid={`output-remove-${task.id}-${idx}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add new */}
            <div className="flex items-center gap-1.5 pt-1 border-t border-slate-100">
              <Input
                type="text"
                placeholder="Paste URL and press +"
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addCurrent();
                  }
                }}
                className="h-8 text-[11px]"
                data-testid={`output-input-${task.id}`}
              />
              <button
                type="button"
                onClick={addCurrent}
                disabled={!newUrl.trim()}
                className="w-8 h-8 rounded-md bg-[#2EB4AC] hover:bg-[#269d96] disabled:bg-slate-200 disabled:cursor-not-allowed text-white flex items-center justify-center transition-colors"
                title="Add to list"
                data-testid={`output-add-row-${task.id}`}
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Save / cancel */}
            <div className="flex items-center justify-end gap-1.5 pt-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setOpen(false)}
                className="h-7 text-[11px] px-2.5"
                data-testid={`output-cancel-${task.id}`}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={save}
                disabled={busy}
                className="h-7 text-[11px] px-3 bg-[#2EB4AC] hover:bg-[#269d96]"
                data-testid={`output-save-${task.id}`}
              >
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Save'}
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Shortcut to Asset Library filtered on this task */}
      {links.length > 0 && (
        <TooltipProvider>
          <Tooltip delayDuration={200}>
            <TooltipTrigger asChild>
              <Link
                to={`/asset-library?q=${encodeURIComponent(task.task_id || '')}`}
                className="inline-flex items-center justify-center w-7 h-7 rounded-md text-slate-400 hover:text-violet-600 hover:bg-violet-50 transition-colors"
                data-testid={`output-library-${task.id}`}
                onClick={(e) => e.stopPropagation()}
              >
                <FolderOpen className="h-3.5 w-3.5" />
              </Link>
            </TooltipTrigger>
            <TooltipContent side="top">View this delivery in the Asset Library</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};

export default OutputLinksCell;
